import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import { isDataChangeApprovalDetails } from "@/features/self/pending/types/pending.types";

export type DataChangeStatus =
  | "pending_hr"
  | "hr_approved"
  | "approved"
  | "hr_rejected"
  | "chief_rejected"
  | "cancelled";

export type SectionName =
  | "general"
  | "contacts"
  | "addresses"
  | "emergency_contacts"
  | "id_documents"
  | "banking"
  | "kyc_documents"
  | "education"
  | "experience"
  | "social_categories";

export type InitiatedBy = "worker" | "hr";

export interface RequestDetailResponse {
  id: number;
  worker_id: number;
  worker_full_name: string;
  status: DataChangeStatus;

  surname: string;
  name: string;
  father_name: string;
  national_id_number: string;
  national_id_issue_date: string | null;
  national_id_expiry_date: string | null;
  national_id_issued_by: string;
  national_id_file: string;
  national_id_file_url: string | null;

  current_values: Partial<Record<SectionName, unknown>>;
  old_values?: Partial<Record<SectionName, unknown>>;
  requested_changes: Partial<Record<SectionName, unknown>>;
  uploaded_files_urls: Record<string, string | null>;
  changed_sections: SectionName[];
  initiated_by: InitiatedBy;

  hr_approved_by_name: string | null;
  hr_approved_at: string | null;
  chief_approved_by_name: string | null;
  chief_approved_at: string | null;
  rejection_reason: string;

  created_at: string;
  updated_at: string;
}

export interface NationalIdDocumentVersion {
  id: number;
  surname: string;
  name: string;
  father_name: string;
  national_id_number: string;
  national_id_issue_date: string;
  national_id_expiry_date: string;
  national_id_issued_by: string;
  national_id_file_view_url: string | null;
  is_current: boolean;
  valid_from: string;
  valid_until: string | null;
  created_at: string;
}

export const dataChangeApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      createDataChangeRequest: builder.mutation<{ id: number }, FormData>({
        query(body) {
          return {
            url: "/api/self/personal-data-change/create/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "SelfService" as const, id: "REQUESTS" },
          { type: "SelfService" as const, id: "PROFILE" },
          "WorkerDetail",
          "PendingActions",
        ],
      }),

      getDataChangeDetail: builder.query<RequestDetailResponse, number>({
        query(id) {
          return {
            url: `/api/hr/personal-data-change/${id}/`,
            method: "GET",
            credentials: "include",
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "DataChangeRequests" as const, id }];
        },
      }),

      hrApproveDataChange: builder.mutation<{ message: string }, number>({
        query(id) {
          return {
            url: `/api/hr/personal-data-change/${id}/hr-approve/`,
            method: "POST",
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, id) {
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            { type: "DataChangeRequests" as const, id },
            "WorkerDetail",
            "PendingActions",
          ];
        },
        async onQueryStarted(id, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "approve_data_change" &&
                  isDataChangeApprovalDetails(item.details) &&
                  item.details.request_id === id,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),

      hrRejectDataChange: builder.mutation<{ message: string }, { id: number; rejection_reason: string }>({
        query({ id, rejection_reason }) {
          return {
            url: `/api/hr/personal-data-change/${id}/hr-reject/`,
            method: "POST",
            body: { rejection_reason },
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            { type: "DataChangeRequests" as const, id },
            "WorkerDetail",
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "approve_data_change" &&
                  isDataChangeApprovalDetails(item.details) &&
                  item.details.request_id === id,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),

      chiefApproveDataChange: builder.mutation<{ message: string }, number>({
        query(id) {
          return {
            url: `/api/hr/personal-data-change/${id}/chief-approve/`,
            method: "POST",
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, id) {
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            { type: "DataChangeRequests" as const, id },
            "WorkerDetail",
            "PendingActions",
          ];
        },
        async onQueryStarted(id, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "approve_data_change_chief" &&
                  isDataChangeApprovalDetails(item.details) &&
                  item.details.request_id === id,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),

      chiefRejectDataChange: builder.mutation<{ message: string }, { id: number; rejection_reason: string }>({
        query({ id, rejection_reason }) {
          return {
            url: `/api/hr/personal-data-change/${id}/chief-reject/`,
            method: "POST",
            body: { rejection_reason },
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            { type: "DataChangeRequests" as const, id },
            "WorkerDetail",
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "approve_data_change_chief" &&
                  isDataChangeApprovalDetails(item.details) &&
                  item.details.request_id === id,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),

      cancelDataChangeRequest: builder.mutation<RequestDetailResponse, number>({
        query(id) {
          return {
            url: `/api/self/personal-data-change/${id}/cancel/`,
            method: "POST",
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, id) {
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            { type: "DataChangeRequests" as const, id },
            "PendingActions",
          ];
        },
      }),

      createDataChangeForWorker: builder.mutation<RequestDetailResponse, FormData>({
        query(body) {
          return {
            url: "/api/hr/personal-data-change/create/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, body) {
          const workerId = Number(body.get("worker_id"));
          return [
            { type: "SelfService" as const, id: "REQUESTS" },
            { type: "SelfService" as const, id: "PROFILE" },
            ...(workerId ? [{ type: "WorkerDetail" as const, id: workerId }] : (["WorkerDetail"] as const)),
            "PendingActions",
          ];
        },
      }),

      getWorkerIdDocumentHistory: builder.query<NationalIdDocumentVersion[], number>({
        query(workerId) {
          return {
            url: `/api/hr/workers/${workerId}/id-document-history/`,
            method: "GET",
            credentials: "include",
          };
        },
        transformResponse(response: NationalIdDocumentVersion[] | { results: NationalIdDocumentVersion[] }) {
          if (Array.isArray(response)) return response;
          return response.results ?? [];
        },
        providesTags(_result, _error, workerId) {
          return [{ type: "WorkerIdDocumentHistory" as const, id: workerId }];
        },
      }),
    };
  },
});

export const {
  useCreateDataChangeRequestMutation,
  useCreateDataChangeForWorkerMutation,
  useGetDataChangeDetailQuery,
  useHrApproveDataChangeMutation,
  useHrRejectDataChangeMutation,
  useChiefApproveDataChangeMutation,
  useChiefRejectDataChangeMutation,
  useCancelDataChangeRequestMutation,
  useGetWorkerIdDocumentHistoryQuery,
} = dataChangeApi;
